"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const pillars = [{
  code: "CRM",
  title: "Communication pipelines",
  description: "Architecting automated client engagement systems that ensure every lead is captured, nurtured, and converted without manual intervention.",
  href: "/ServicesPage"
}, {
  code: "FSM",
  title: "Operation tracking",
  description: "Building robust field service management frameworks that provide real-time visibility into project status, resource allocation, and site progress.",
  href: "/ServicesPage"
}, {
  code: "FIN",
  title: "Automated bookkeeping",
  description: "Integrating seamless financial infrastructure that synchronizes invoicing, expense tracking, and cash flow management into one unified ledger.",
  href: "/ServicesPage"
}];
export default function Solutions() {
  const intro = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.2
  });
  const row = useRevealOnScroll<HTMLDivElement>({
    once: true,
    threshold: 0.15
  });
  return <section data-section-id="10650" id="backend-solutions" className="bg-background text-foreground py-32 md:py-40">
      <div className="mx-auto w-full max-w-[1440px] px-6 md:px-12 lg:px-20">
        {}
        <div ref={intro.ref} className={`grid grid-cols-1 md:grid-cols-12 gap-x-6 gap-y-8 transition-all duration-700 ease-out ${intro.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`}>
          <div className="md:col-span-4">
            <h2 className="font-default text-foreground" style={{
            fontSize: "clamp(2.25rem, 4vw, 4rem)",
            fontWeight: 500,
            letterSpacing: "-0.022em",
            lineHeight: 1.02
          }}>
              Three pillars. <em className="italic font-normal">One</em> digital framework.
            </h2>
          </div>

          <div className="md:col-span-8 md:col-start-5">
            <div className="md:max-w-[85%]">
              <p className="text-muted-foreground" style={{
              fontSize: "clamp(1.125rem, 1.3vw, 1.375rem)",
              fontWeight: 400,
              letterSpacing: "-0.005em",
              lineHeight: 1.5
            }}>
                CRM, field service management, and automated bookkeeping are not separate
                tools at CSOS — they are the structural foundation of your trade business. 
                Each system we build is designed to interlock, creating a cohesive 
                digital environment that scales with your growth, ensuring data 
                integrity and operational precision across every job site.
              </p>
            </div>
          </div>
        </div>

        {}
        <div className="mt-16 md:mt-20 h-px w-full bg-border" />

        {}
        <div ref={row.ref} className="mt-12 md:mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
          {pillars.map((pillar, i) => <article key={pillar.code} data-index={i} className={`group md:col-span-4 bg-background border border-border p-10 transition-all duration-700 ease-out ${row.isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-4"}`} style={{
          transitionDelay: row.isVisible ? `${i * 60}ms` : "0ms"
        }}>
              {}
              <div className="md:hidden mb-4">
                <span className="font-mono uppercase text-primary" style={{
              fontSize: "0.75rem",
              fontWeight: 700,
              letterSpacing: "0.04em",
              lineHeight: 1
            }}>
                  {pillar.code}
                </span>
              </div>

              {}
              <div className="md:grid md:grid-cols-12 md:gap-4">
                <div className="hidden md:block md:col-span-3">
                  <span className="font-mono uppercase text-primary" style={{
                fontSize: "0.75rem",
                fontWeight: 700,
                letterSpacing: "0.04em",
                lineHeight: 1
              }}>
                    {pillar.code}
                  </span>
                </div>

                <div className="md:col-span-9 flex flex-col">
                  <h3 className="font-default text-foreground" style={{
                fontSize: "clamp(1.5rem, 2vw, 2rem)",
                fontWeight: 500,
                letterSpacing: "-0.015em",
                lineHeight: 1.15
              }}>
                    {pillar.title}
                  </h3>

                  <div className="relative mt-6 mb-6 h-px w-full bg-border overflow-hidden">
                    <span className="absolute inset-y-0 left-0 w-[60%] bg-primary transition-all duration-300 ease-out group-hover:w-full" />
                  </div>

                  <p className="text-muted-foreground" style={{
                fontSize: "1rem",
                fontWeight: 400,
                lineHeight: 1.6
              }}>
                    {pillar.description}
                  </p>

                  <div className="mt-10">
                    <Link to={pillar.href} className="inline-flex items-center gap-2 text-foreground underline underline-offset-4 decoration-foreground hover:decoration-primary transition-colors">
                      <span style={{
                    fontSize: "1rem",
                    fontWeight: 500
                  }}>
                        View infrastructure
                      </span>
                      <ArrowRight className="size-4 transition-transform duration-300 ease-out group-hover:translate-x-2" strokeWidth={1.5} />
                    </Link>
                  </div>
                </div>
              </div>
            </article>)}
        </div>
      </div>
    </section>;
}